if (!customElements.get('quick-cart-drawer')) {
  class QuickCartDrawer extends HTMLElement {
    constructor() {
      super();
      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', this.init.bind(this));
        this.parentElement.addEventListener('shopify:section:select', () => this.open());
        this.parentElement.addEventListener('shopify:section:deselect', () => this.close());
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.toggleState = false;

      if (!this.classList.contains('is--open')) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }

      this.querySelector('.button--close').addEventListener('click', this.close.bind(this));
      this.querySelector('.quick-cart-drawer__backdrop').addEventListener('click', this.close.bind(this));

      this.querySelector('.quick-cart-drawer__blocks').addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.toggleState) {
          this.close();
        }

        if (this.toggleState) {
          const focusableElements = this.querySelectorAll(
            'button, [href], input, select, label, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstFocusableElement = focusableElements[0];
          const lastFocusableElement = focusableElements[focusableElements.length - 1];

          let isTabPressed = e.key === 'Tab';

          if (!isTabPressed) {
            return;
          }

          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              e.preventDefault();
              lastFocusableElement.focus();
            }
          } else {
            if (document.activeElement === lastFocusableElement) {
              e.preventDefault();
              firstFocusableElement.focus();
            }
          }
        }
      });

      this.initTriggers();
    }

    initTriggers() {
      const bindTrigger = element => {
        if (!element._init) {
          element.addEventListener('click', event => {
            this.fetchProductForQuickCartDrawer(event);
          });
          element._init = true;
        }
      };

      document.querySelectorAll('.quick-cart-drawer__trigger').forEach(bindTrigger);

      const observer = new MutationObserver(() => {
        document.querySelectorAll('.quick-cart-drawer__trigger').forEach(bindTrigger);
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    async fetchProductForQuickCartDrawer(event) {
      const trigger = event.target.closest('[data-product-url]');
      if (!trigger || trigger.classList.contains('is--loading')) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const productUrl = trigger.dataset.productUrl;
      trigger.classList.add('is--loading');
      trigger.setAttribute('aria-busy', 'true');

      try {
        const variantId =
          trigger.dataset.variantId ||
          this.getVariantIdFromProductCard(trigger) ||
          await this.fetchFirstAvailableVariantId(productUrl);

        if (!variantId) {
          throw new Error('No available variant found for quick add.');
        }

        const addPayload = typeof getCartAddPayloadWithShippingProtection === 'function'
          ? getCartAddPayloadWithShippingProtection({
            id: Number(variantId),
            quantity: 1
          })
          : {
            id: Number(variantId),
            quantity: 1
          };

        const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify(addPayload)
        });

        const addResult = await addResponse.json().catch(() => null);

        if (!addResponse.ok || addResult?.errors) {
          throw new Error(addResult?.description || addResult?.message || 'Product could not be added to cart.');
        }

        if (typeof updateCartCounters === 'function') {
          await updateCartCounters();
        } else {
          await this.updateCartCount();
        }

        const newDrawer = await this.rerenderCartDrawer();
        this.openCartDrawer(newDrawer);
      } catch (error) {
        console.error(error);

        if (productUrl) {
          window.location.href = productUrl;
        }
      } finally {
        trigger.classList.remove('is--loading');
        trigger.removeAttribute('aria-busy');
      }
    }

    getVariantIdFromProductCard(trigger) {
      const productCard = trigger.closest('product-card');
      return productCard?.querySelector('input[name="id"]')?.value;
    }

    getProductJsonUrl(productUrl) {
      if (!productUrl) return null;

      let productPath = productUrl;

      try {
        productPath = new URL(productUrl, window.location.origin).pathname;
      } catch (error) {
        productPath = productUrl;
      }

      productPath = productPath.split('?')[0].split('#')[0].replace(/\/$/, '');

      if (!productPath) return null;
      if (productPath.endsWith('.js')) return productPath;

      const routeRoot = window.Shopify?.routes?.root || '/';
      const normalizedRoot = routeRoot.replace(/\/$/, '');

      if (routeRoot !== '/' && productPath.startsWith('/products/')) {
        productPath = `${normalizedRoot}${productPath}`;
      }

      return `${productPath}.js`;
    }

    async fetchFirstAvailableVariantId(productUrl) {
      const productJsonUrl = this.getProductJsonUrl(productUrl);
      if (!productJsonUrl) return null;

      const response = await fetch(productJsonUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });

      if (!response.ok) return null;

      const product = await response.json();
      const variant = product.variants?.find(item => item.available) || product.variants?.[0];

      return variant?.id;
    }

    async updateCartCount() {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      const cart = await response.json();
      const itemCount = typeof getCartItemCountExcludingShippingProtection === 'function'
        ? getCartItemCountExcludingShippingProtection(cart)
        : cart.item_count || 0;

      document.querySelectorAll(
        '.cart-count, .cart-count-badge, .cart-count-bubble span, [data-cart-count], .header__cart-count, .cart-drawer__title-counter'
      ).forEach(el => {
        el.textContent = itemCount;
      });
    }

    async rerenderCartDrawer() {
      const response = await fetch(`${window.Shopify.routes.root}?section_id=cart-drawer&_=${Date.now()}`, {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin'
      });

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('cart-drawer');
      const currentDrawer = document.querySelector('cart-drawer');

      if (!newDrawer || !currentDrawer) return null;

      currentDrawer.replaceWith(newDrawer);
      if (window.customElements && customElements.upgrade) {
        customElements.upgrade(newDrawer);
      }
      return newDrawer;
    }

    openCartDrawer(drawerEl) {
      const drawer = drawerEl || document.querySelector('cart-drawer');
      if (!drawer) return;

      if (this.classList.contains('is--open')) {
        this.close();
      }

      if (typeof drawer.open === 'function') {
        requestAnimationFrame(() => {
          drawer.open();
        });
        return;
      }

      drawer.classList.add('is-visible');
    }

    setActiveMedia(id) {
      const mediaFound = Array.from(this.querySelectorAll('[data-media-id]')).find(media => Number(media.dataset.mediaId) === id);
      if (mediaFound) {
        this.sliderInstance.slideTo(Number(mediaFound.dataset.index));
      }
    }

    /** Drawer coreÄ‚Ĺş functions */
    toggle() {
      if (!this.toggleState) {
        this.open();
      } else {
        this.close();
      }
    }

    open() {
      this.toggleState = true;
      document.querySelector('body').classList.add('overflow-hidden');
      const quickCartDrawer = document.querySelector('.quick-cart-drawer__blocks');
      const closeButton = quickCartDrawer.querySelector('.button--close');
      quickCartDrawer.setAttribute('tabindex', '0');
      closeButton.setAttribute('tabindex', '0');
      this.classList.add('is--open');
      this.opened();

      const firstFocusableElement = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }

    close() {
      this.toggleState = false;

      const shopTheLookDrawer = document.querySelector('shop-the-look-drawer');

      if (shopTheLookDrawer && !shopTheLookDrawer.classList.contains('is--open')) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }
      if (!shopTheLookDrawer) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }
      this.classList.remove('is--open');
      this.closed();
      this.toggleAriaExpanded();

      /** start autoplay on quick cart drawe close */
      if (document.querySelector('card-product-slider.product--open-on-quick-cart')) {
        document.querySelector('card-product-slider.product--open-on-quick-cart').slider.autoplay.start();
        document.querySelector('card-product-slider.product--open-on-quick-cart').classList.remove('product--open-on-quick-cart');
      }
    }

    toggleAriaExpanded(event) {
      if (event) {
        if (event.target.closest('button')) event.target.closest('button').setAttribute('aria-expanded', true);
        this.querySelector('.button--close').setAttribute('aria-expanded', true);
      } else {
        document.querySelectorAll('[aria-controls="quick-cart-drawer"]').forEach(button => {
          button.setAttribute('aria-expanded', false);
        });
      }
    }

    opened() {
      const openedEvent = new Event('opened', { bubbles: true });
      this.dispatchEvent(openedEvent);
    }

    closed() {
      const closedEvent = new Event('closed', { bubbles: true });
      this.dispatchEvent(closedEvent);
    }
  }

  customElements.define('quick-cart-drawer', QuickCartDrawer);
}